import Grid from "@mui/material/Grid2";
import { Box, FormControl, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { Control, UseFormClearErrors, useWatch } from "react-hook-form";

import {
  displayFlex,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import { MyAccountModel } from "../../../models/MyAccount";
import ReadOnlyField from "../../../components/FormPriorAuthReAuth/ReadOnlyField";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import { TabSessionDataReAuthResponse } from "../../../models/api/TabSessionStorage";
import { ReAuthTabData } from "../../../models/TabSessionStorage";
import { FieldSpacing } from "../../PriorAuthWorkFlow/styles/style";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import { ReAuthDefaultReferralDetails, ReAuthDefaultServiceProviderDetails } from "../../../models/Referral";
import { ListOptions } from "../../../models/Master";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { rules } from "../../../utils/validation/Validation";
import { FormInputDropdown } from "../../../components/FormPriorAuthReAuth/FormInputDropdown";
import { getApprovedAuthValueByLabel } from "../../../utils";
import { Numbers } from "../../../constants/Referral";

export interface PropsFromState {
  loggedInUser: MyAccountModel;
  TabSessionStorage: TabSessionDataReAuthResponse;
  control: Control<any>;
  setValue: (id: any, value: any, boolean?: any) => void;
  getValues: any;
  reset: any;
  reAuthServiceProviderState: ReAuthDefaultServiceProviderDetails;
  masterSendCommunicationAuthTypeState: ListOptions[];
  reAuthReferralDetailState: ReAuthDefaultReferralDetails;
  clearErrors: UseFormClearErrors<ReAuthTabData>;
}

export interface PropsFromDispatch {
  saveTabSessionStorage: (sessionData: ReAuthTabData) => void;
  getSendCommunicationAuthType: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AgencyDetails: React.FC<AllProps> = ({
  control,
  getValues,
  reset,
  loggedInUser,
  reAuthServiceProviderState,
  masterSendCommunicationAuthTypeState,
  getSendCommunicationAuthType,
  reAuthReferralDetailState,
  setValue,
  clearErrors
}) => {
  const prevApprovedAuthPreference = useRef<number | null>(null);
  const isInitialLoad = useRef(true);
  
  const sp = reAuthServiceProviderState as any;
  useEffect(() => {
    reset({
      ...getValues(),
      reAuthAgencyDetails: {
        ...getValues("reAuthAgencyDetails"),
        requesterName: loggedInUser.providerPortalUser.userName,
        requesterEmail: loggedInUser.providerPortalUser.email,
        serviceProviderId: sp.serviceProviderId,
        facilityName: sp.providerName,
        requesterNpi: sp.providerNpi,
        streetName1: sp.streetName1,
        streetName2: sp.streetName2,
        city: sp.providerCity,
        requesterState: sp.providerState,
        zipCode: sp.zipCode,
        requesterFax: sp.fax,
        requesterPhoneNo: sp.phone,
        sendApprovedAuthTo:
          getValues("reAuthAgencyDetails.sendApprovedAuthTo") ||
          getApprovedAuthValueByLabel(reAuthReferralDetailState.sendApprovedAuthTo)
      },
    });
  }, [reAuthServiceProviderState]);

  const [
    streetName1,
    streetName2,
    city,
    requesterState,
    zipCode,
    requesterPhoneNo,
    requesterFax,
    requesterName,
  ] = useWatch({
    control,
    name: [
      "reAuthAgencyDetails.streetName1",
      "reAuthAgencyDetails.streetName2",
      "reAuthAgencyDetails.city",
      "reAuthAgencyDetails.requesterState",
      "reAuthAgencyDetails.zipCode",
      "reAuthAgencyDetails.requesterPhoneNo",
      "reAuthAgencyDetails.requesterFax",
      "reAuthAgencyDetails.requesterName",
      "reAuthAgencyDetails.requesterEmail",
      "reAuthAgencyDetails.sendApprovedAuthTo",
    ],
  });

  useEffect(() => {
    if (masterSendCommunicationAuthTypeState.length === 0) {
      getSendCommunicationAuthType();
    }
    reset({
      ...getValues(),
      reAuthTabStatus: updateTabStatus(0, getValues("reAuthTabStatus")),
    });
  }, []);

  const approvedAuthPreference = useWatch({
    control,
    name: "reAuthAgencyDetails.sendApprovedAuthTo",
  });

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    } else if (
      prevApprovedAuthPreference.current !== null &&
      prevApprovedAuthPreference.current !== approvedAuthPreference
    ) {
      setValue("reAuthAgencyDetails.secondaryEmailAddress", "");
      clearErrors("reAuthAgencyDetails.secondaryEmailAddress");
    }
    prevApprovedAuthPreference.current = approvedAuthPreference;
  }, [approvedAuthPreference]);

  return (
    <>
      <Grid size={12}>
        <Grid container>
          <Grid size={12} sx={flexColumn}>
            <Grid container spacing={2} sx={flexRow}>
              <Grid size={{ xs: 7 }} sx={FieldSpacing}>
                <ReadOnlyField
                  label={"Accepting Agency Name/Branch:"}
                  value={`${sp.providerName}\nNPI: ${sp.providerNpi}, ${sp.providerAddress}`}
                  isReauthAgency={true}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  label={"Agency NPI:"}
                  value={getValues("reAuthAgencyDetails.requesterNpi")}
                />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <ReadOnlyField
                  value={requesterName}
                  label="Agency Contact Name: "
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency Street 1:"} value={streetName1} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <ReadOnlyField label="Agency Phone:" value={requesterPhoneNo} />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField
                  label={"Agency Street 2:"}
                  value={streetName2?.trim() ? streetName2.trim() : "-"}
                />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <ReadOnlyField label="Agency Fax:" value={requesterFax} />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow} mt={2}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency City:"} value={city} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <FormInputText
                  control={control}
                  helper={rules.email}
                  name={"reAuthAgencyDetails.requesterEmail"}
                  label="Agency Email Address: "
                  textLength={128}
                  disabled={false}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency State:"} value={requesterState} />
              </Grid>
              <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body1"
                    fontWeight={fontWeight[2]}
                    color={colors.fonts[2]}
                  >
                    Send Approved Auth to:
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={fontWeight[2]}
                    color={colors.red[100]}
                    pl={"0.3rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body2"
                    fontWeight={fontWeight[2]}
                    color={colors.fonts[2]}
                    style={{ fontStyle: "italic" }}
                  >
                    *Documentation including PHI may be sent to the choice below
                  </Typography>
                </Box>
                <Box pt={0.8}>
                  <FormControl variant="standard" fullWidth>
                    <FormInputDropdown
                      control={control}
                      name={"reAuthAgencyDetails.sendApprovedAuthTo"}
                      list={masterSendCommunicationAuthTypeState}
                      arrowDropDownIcon={false}
                      helper={rules.sendApprovedAuthTo}
                      mandatorySymbol={true}
                    />
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={flexRow}>
              <Grid size={{ xs: 4.5 }} sx={FieldSpacing}>
                <ReadOnlyField label={"Agency Zip:"} value={zipCode} />
              </Grid>
              {approvedAuthPreference === Numbers.ONE && (
                <Grid size={{ xs: 4 }} sx={FieldSpacing}>
                  <Box sx={displayFlex}>
                    <Typography
                      variant="body1"
                      fontWeight={fontWeight[2]}
                      color={colors.fonts[2]}
                    >
                      Notification of Decisions:
                    </Typography>
                  </Box>
                  <Box sx={displayFlex}>
                    <Typography
                      variant="body2"
                      fontWeight={fontWeight[2]}
                      color={colors.fonts[2]}
                      style={{ fontStyle: "italic" }}
                    >
                      *An email will be sent when your request has a decision. A secondary email address may be provided below for this notification.
                    </Typography>
                  </Box>
                  <Box sx={displayFlex} pt={0.8}>
                    <FormInputText
                      control={control}
                      helper={rules.emailPattern}
                      name={"reAuthAgencyDetails.secondaryEmailAddress"}
                      textLength={64}
                      disabled={false}
                      label="Secondary Address"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AgencyDetails;
